/* global __CONFIG__ */

import { all, takeLatest, call, put, take, select } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import PubNub from 'pubnub';

import { TYPE_TO_DETERMINE_PERSONAL_CHANNEL } from '@coconut/constants/pubnubChannel';

import {
  types,
  pubnubSubscribe,
  pubnubUnsubscribe,
  pubnubSubscribeSuccess,
  pubnubSubscribeFailure,
  pubnubUnsubscribeSuccess,
  pubnubUnsubscribeFailure,
  pubnubResubscribeSuccess,
  pubnubResubscribeFailure,
} from '../redux/pubnubQuestionList';
import { fetchActiveQuestions } from '@coconut/scenes/Home/services/redux/activeQuestions';
import NewMessageSound from '@coconut/audio/beep.mp3';
import {
  newMessage,
  getNewMessageCount,
} from '@coconut/scenes/Chat/services/redux/chatsMessage';
import { getRole } from '@coconut/services/redux/profile';
import { STUDENT } from '@coconut/constants/userRole';
import {
  fetchQuestions,
  updateAcceptedQuestionId,
} from '@coconut/services/redux/questionList';
import {
  getPersonalPubnubChannel,
  getWebOpendQaNotifyPubnubChannel,
} from '@coconut/services/redux/app';
import { queryOpenQuestions } from '@coconut/services/redux/openQuestion';
import {
  CANCEL_ACTION,
  QUESTION_ACCEPT,
  NEW_QUESTION_HAS_BEEN_OPENED,
  MATCHING_WIN,
} from '@coconut/scenes/Chat/services/constants/messageActions';

import setPageTitleIcon from '@coconut/utils/setPageTitleIcon';

const newMessageAudio = new Audio(NewMessageSound);

const pubnub = new PubNub({
  subscribeKey: __CONFIG__.PUBNUB_SUBSCRIBE_KEY,
});

function* subscribe({ channels, userId, userRole }) {
  const personalChannel = yield select(getPersonalPubnubChannel);
  const webOpendQaNotifyPubnubChannel = yield select(
    getWebOpendQaNotifyPubnubChannel
  );

  return eventChannel((emit) => {
    pubnub.setUUID(userId);

    pubnub.subscribe({
      channels: [...channels, personalChannel, webOpendQaNotifyPubnubChannel],
    });

    pubnub.addListener({
      message: (msg) => {
        const { message } = msg;
        const parsedMsg = JSON.parse(message);

        const isStudent = userRole === STUDENT;

        if (isStudent) {
          emit(fetchActiveQuestions(userId));
        } else {
          // TUTOR
          if (parsedMsg.message_action === CANCEL_ACTION) {
            emit(queryOpenQuestions());
            return;
          }

          // personal and chat room pubnub has different format
          const { type, data = {} } = parsedMsg;
          emit(fetchQuestions());

          if (type === TYPE_TO_DETERMINE_PERSONAL_CHANNEL) {
            const { question_id: questionId, status } = data;

            if (status === QUESTION_ACCEPT || status === MATCHING_WIN) {
              emit(updateAcceptedQuestionId(questionId));
            }
          }

          if (data.status === NEW_QUESTION_HAS_BEEN_OPENED) {
            emit(queryOpenQuestions());
          }
        }

        emit(newMessage());
        newMessageAudio.play();
      },
    });

    return () => {};
  });
}

function unsubscribe() {
  return pubnub.unsubscribeAll();
}

function* pubnubSubscribeFlow(action) {
  const { userId, channels } = action.payload;
  const userRole = yield select(getRole);

  try {
    const channel = yield call(subscribe, {
      channels,
      userId,
      userRole,
    });

    yield put(pubnubSubscribeSuccess(channels));

    while (true) {
      const action = yield take(channel);
      yield put(action);

      const messageCount = yield select(getNewMessageCount);
      setPageTitleIcon(messageCount);
    }
  } catch (err) {
    yield put(pubnubSubscribeFailure(err.message));
  }
}

function* pubnubUnsubscribeFlow() {
  try {
    yield call(unsubscribe);

    yield put(pubnubUnsubscribeSuccess());
  } catch (err) {
    yield put(pubnubUnsubscribeFailure(err.message));
  }
}

function* pubnubResubscribeFlow(action) {
  const { userId, channels } = action.payload;

  const userRole = yield select(getRole);

  try {
    yield put(pubnubUnsubscribe());

    yield put(
      pubnubSubscribe({
        channels,
        userId,
        userRole,
      })
    );

    yield put(pubnubResubscribeSuccess());
  } catch (err) {
    yield put(pubnubResubscribeFailure(err.message));
  }
}

export default function* () {
  yield all([
    takeLatest(types.PUBNUB_SUBSCRIBE, pubnubSubscribeFlow),
    takeLatest(types.PUBNUB_UNSUBSCRIBE, pubnubUnsubscribeFlow),
    takeLatest(types.PUBNUB_RESUBSCRIBE, pubnubResubscribeFlow),
  ]);
}



// WEBPACK FOOTER //
// ./src/services/sagas/pubnubQuestionList.js